import { useEffect, useRef, useState } from "hono/jsx";
import { VoxelViewNotFound } from "./views/VoxelViewNotFound.tsx";
import { VoxelMenu } from "./VoxelMenu.tsx";

const BLOCK_TYPES = [
  "GRASS",
  "DIRT",
  "STONE",
  "WOOD",
  "LEAVES",
  "GLASS",
  "SAND",
  "BRICK",
  "WATER",
  "BEDROCK",
];

interface OtherPlayer {
  id: string;
  name: string;
  color: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number };
}

export const BabylonGame = (
  { roomId, user }: {
    roomId?: string;
    user?: { id: string; name?: string; isAnonymous?: boolean };
  },
) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const myPlayerIdRef = useRef<string>("");
  const [players, setPlayers] = useState<OtherPlayer[]>([]);
  const [connected, setConnected] = useState(false);
  const [isSprinting, setIsSprinting] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const selectedIdxRef = useRef<number>(0);

  // Menu State
  const [showMenu, setShowMenu] = useState(false);
  const [roomState, setRoomState] = useState<
    { isPublic: boolean; isOwner: boolean }
  >({ isPublic: false, isOwner: false });

  // Room Not Found State
  const [roomNotFound, setRoomNotFound] = useState<
    boolean | { found: false; isReserved?: boolean; isOwner?: boolean }
  >(false);
  const [retryCount, setRetryCount] = useState(0);

  // Sync ref
  useEffect(() => {
    selectedIdxRef.current = selectedIdx;
  }, [selectedIdx]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Only toggle if not in "Not Found" state
        if (!roomNotFound) {
          setShowMenu((prev) => !prev);

          // Unlock pointer if menu is opening
          if (!showMenu && document.pointerLockElement) {
            document.exitPointerLock();
          }
        }
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, [roomNotFound, showMenu]);

  // Setup global handlers for Babylon to call into React
  useEffect(() => {
    // deno-lint-ignore no-explicit-any
    (globalThis as any).updateHotbar = (idx: number) => {
      setSelectedIdx(idx);
    };

    // deno-lint-ignore no-explicit-any
    (globalThis as any).updateSprintState = (sprinting: boolean) => {
      setIsSprinting(sprinting);
    };
  }, []);

  const switchBlock = (idx: number) => {
    const newIdx = ((idx % BLOCK_TYPES.length) + BLOCK_TYPES.length) %
      BLOCK_TYPES.length;
    setSelectedIdx(newIdx);
    // deno-lint-ignore no-explicit-any
    if ((globalThis as any).setBlockType) {
      // deno-lint-ignore no-explicit-any
      (globalThis as any).setBlockType(BLOCK_TYPES[newIdx]);
    }
  };

  useEffect(() => {
    console.log("BabylonGame: Component Mounted");
    if (roomNotFound) return; // Don't init game if room not found

    const init = () => {
      console.log("BabylonGame: Init triggered");
      // deno-lint-ignore no-explicit-any
      if ((globalThis as any).initBabylonGame && canvasRef.current) {
        console.log("BabylonGame: calling initBabylonGame");
        // deno-lint-ignore no-explicit-any
        (globalThis as any).initBabylonGame(canvasRef.current);
        switchBlock(0);

        if (roomId) {
          connectMultiplayer();
        }
      }
    };

    const connectMultiplayer = () => {
      const protocol = globalThis.location.protocol === "https:"
        ? "wss:"
        : "ws:";
      const wsUrl =
        `${protocol}//${globalThis.location.host}/ws/voxel?room=${roomId}`;
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        setConnected(true);
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleServerMessage(msg);
        } catch (e) {
          console.error("Failed to parse WS message", e);
        }
      };

      socket.onclose = () => {
        setConnected(false);
      };

      // deno-lint-ignore no-explicit-any
      (globalThis as any).sendMultiplayerMessage = (
        type: string,
        data: any,
      ) => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type, ...data }));
        }
      };
    };

    // deno-lint-ignore no-explicit-any
    const handleServerMessage = (msg: any) => {
      switch (msg.type) {
        case "WELCOME":
          myPlayerIdRef.current = msg.playerId;
          break;
        case "ROOM_NOT_FOUND":
          setRoomNotFound({
            found: false,
            isReserved: msg.isReserved,
            isOwner: msg.isOwner,
          });
          if (wsRef.current) wsRef.current.close();
          break;
        case "ROOM_STATE": {
          setRoomState({
            isPublic: !!msg.state.isPublic,
            isOwner: msg.state.hostId === myPlayerIdRef.current ||
              !!(user && user.id && msg.state.ownerId === user.id),
          });

          const otherPlayers = msg.state.players.filter((p: OtherPlayer) =>
            p.id !== myPlayerIdRef.current
          );
          setPlayers(otherPlayers);
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).updateOtherPlayers) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).updateOtherPlayers(otherPlayers);
          }
          break;
        }
        case "PLAYER_JOINED":
          setPlayers((prev) => [
            ...prev.filter((p) => p.id !== msg.player.id),
            msg.player,
          ]);
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).addOtherPlayer) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).addOtherPlayer(msg.player);
          }
          break;
        case "PLAYER_LEFT":
          setPlayers((prev) => prev.filter((p) => p.id !== msg.playerId));
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).removeOtherPlayer) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).removeOtherPlayer(msg.playerId);
          }
          break;
        case "PLAYER_MOVED":
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).moveOtherPlayer) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).moveOtherPlayer(
              msg.playerId,
              msg.position,
              msg.rotation,
            );
          }
          break;
        case "BLOCK_PLACED":
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).placeBlockRemote) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).placeBlockRemote(msg.position, msg.blockType);
          }
          break;
        case "BLOCK_BROKEN":
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).breakBlockRemote) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).breakBlockRemote(msg.position);
          }
          break;
        case "WORLD_SYNC":
          // Apply all world changes when joining a room
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).applyWorldChanges) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).applyWorldChanges(msg.changes);
          }
          break;
        case "CHUNK_DATA":
          // Apply chunk data updates
          // deno-lint-ignore no-explicit-any
          if ((globalThis as any).applyWorldChanges) {
            // deno-lint-ignore no-explicit-any
            (globalThis as any).applyWorldChanges(msg.updates);
          }
          break;
        case "ERROR":
          console.error("Server error:", msg.message);
          break;
      }
    };

    // Expose hotbar controls to Babylon engine
    // deno-lint-ignore no-explicit-any
    (globalThis as any).selectHotbarSlot = (idx: number) => {
      switchBlock(idx);
    };
    // deno-lint-ignore no-explicit-any
    (globalThis as any).scrollHotbar = (delta: number) => {
      switchBlock((selectedIdxRef.current ?? 0) + delta);
    };

    // deno-lint-ignore no-explicit-any
    if ((globalThis as any).initBabylonGame) {
      init();
    } else {
      globalThis.addEventListener("babylon-ready", init, { once: true });
    }

    return () => {
      globalThis.removeEventListener("babylon-ready", init);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [roomId, retryCount, roomNotFound]);

  // Setup global handlers for Babylon to call into React
  useEffect(() => {
    // deno-lint-ignore no-explicit-any
    (globalThis as any).updateHotbar = (idx: number) => {
      setSelectedIdx(idx);
    };

    // deno-lint-ignore no-explicit-any
    (globalThis as any).updateSprintState = (sprinting: boolean) => {
      setIsSprinting(sprinting);
    };
  }, [selectedIdx]); // Dependency array should include setIsSprinting if it's used directly, but it's a setter, so it's stable.

  if (roomNotFound) {
    const info = typeof roomNotFound === "object"
      ? roomNotFound
      : { found: false, isReserved: false, isOwner: false };
    const canCreate = user && !user.isAnonymous;

    return (
      <VoxelViewNotFound
        roomId={roomId || ""}
        info={info as any}
        canCreate={!!canCreate}
        onCreate={async () => {
          try {
            const res = await fetch(`/api/voxel/rooms`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                customId: roomId,
              }),
            });
            if (res.ok) {
              setRoomNotFound(false);
              setRetryCount((prev) => prev + 1);
            }
          } catch (err) {
            console.error("Failed to create room", err);
          }
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <VoxelMenu
        show={showMenu}
        isPublic={roomState.isPublic}
        isOwner={roomState.isOwner}
        isAnonymous={user?.isAnonymous}
        onClose={() => {
          setShowMenu(false);
          // Re-lock pointer?
          if (canvasRef.current) canvasRef.current.requestPointerLock();
        }}
        onTogglePublic={(val) => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: "UPDATE_SETTINGS",
              settings: { isPublic: val },
            }));
            // Optimistic update
            setRoomState((prev) => ({ ...prev, isPublic: val }));
          }
        }}
      />

      {/* Crosshair */}
      <div id="crosshair"></div>

      {/* Connection Status */}
      {roomId && (
        <div
          style={{
            position: "absolute",
            top: "0.5rem",
            left: "0.5rem",
            zIndex: 300,
          }}
        >
          <div
            class={`badge ${
              connected ? "badge-success" : "badge-error"
            } font-bold`}
          >
            {connected ? `Room: ${roomId}` : "Disconnected"}
          </div>
          <div class="text-xs text-white/50 mt-1">
            {players.length + 1} players
          </div>
        </div>
      )}

      {/* Sprint Indicator */}
      {isSprinting && (
        <div
          style={{
            position: "absolute",
            bottom: "60px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            background: "rgba(0,0,0,0.5)",
            padding: "4px 12px",
            borderRadius: "16px",
            zIndex: 250,
            color: "white",
            fontSize: "0.8rem",
            fontWeight: "bold",
            animation: "pulse 1s infinite",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 10h-2l-3-6-3 6h-7" />
            <circle cx="15.5" cy="18.5" r="2.5" />
            <path d="M10 16v-6" />
            <path d="M13 13l-4-4" />
          </svg>
          RUNNING
        </div>
      )}

      {/* Hotbar - no padding, 10 slots */}
      <div
        id="hotbar"
        style={{
          position: "absolute",
          bottom: "0",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: "0",
          padding: "0",
          background: "rgba(0,0,0,0.5)",
          zIndex: 200,
        }}
      >
        {BLOCK_TYPES.map((type, i) => (
          <div
            key={type}
            style={{
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              position: "relative",
              background: selectedIdx === i
                ? "rgba(255,255,255,0.3)"
                : "transparent",
              border: selectedIdx === i
                ? "2px solid white"
                : "1px solid rgba(255,255,255,0.2)",
            }}
            onClick={() => switchBlock(i)}
          >
            <div
              class={`block-icon icon-${type}`}
              style={{ width: "32px", height: "32px" }}
            >
            </div>
            <span
              style={{
                position: "absolute",
                bottom: "2px",
                right: "4px",
                fontSize: "10px",
                color: "white",
                textShadow: "1px 1px 1px black",
              }}
            >
              {i === 9 ? "0" : i + 1}
            </span>
          </div>
        ))}
      </div>

      <canvas ref={canvasRef} id="renderCanvas" />
    </div>
  );
};
